import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  proDriverAgreementTemplateListQuery,
  ProDriverAgreementTemplateRecord,
} from "@/app/ProDrivers/Agreements/GraphQL/agreementTemplateQueries";
import {
  archiveProDriverAgreementTemplate,
  generateProDriverAgreementTemplatePdf,
} from "@/app/ProDrivers/Agreements/GraphQL/agreementTemplateMutations";
import { AgreementTemplateDialog } from "@/app/ProDrivers/Agreements/Components/Dialogs/AgreementTemplateDialog";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { Button } from "@/components/catalyst/button";
import { Badge } from "@/components/catalyst/badge";
import {
  PlusIcon,
  PencilIcon,
  ArchiveBoxIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/20/solid";
import { formatDate } from "@/lib/Formatters/formatDate";
import { toast } from "sonner";

export function AgreementTemplatesScreen() {
  const { data } = useGqlQuery(proDriverAgreementTemplateListQuery, {});
  const { open: openTemplateDialog } = useDialog(AgreementTemplateDialog);

  const templates = data?.proDriverAgreementTemplates ?? [];

  return (
    <div className="space-y-4 py-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Agreement templates
          </h2>
          <p className="text-sm text-gray-500">
            The standard wording used to build a driver agreement for a drive.
          </p>
        </div>
        <Button onClick={() => openTemplateDialog({})}>
          <PlusIcon className="size-4" />
          New template
        </Button>
      </div>

      {templates.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
          No templates yet
        </p>
      ) : (
        <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
          {templates.map((template) => (
            <TemplateRow key={template.id} template={template} />
          ))}
        </ul>
      )}
    </div>
  );
}

function TemplateRow({
  template,
}: {
  template: ProDriverAgreementTemplateRecord;
}) {
  const { open: openTemplateDialog } = useDialog(AgreementTemplateDialog);
  const { open: openConfirm } = useConfirmDeleteDialog();
  const { mutateAsync: archiveTemplate } = useGqlMutation(
    archiveProDriverAgreementTemplate,
  );
  const { mutateAsync: generatePdf, isPending: isGeneratingPdf } =
    useGqlMutation(generateProDriverAgreementTemplatePdf);

  async function handleDownload() {
    const res = await generatePdf({ id: template.id });

    window.open(
      res.generateProDriverAgreementTemplatePdf.url,
      "_blank",
      "noopener,noreferrer",
    );
  }

  function handleArchive() {
    openConfirm({
      title: "Archive template",
      message: `Archive "${template.name}"? It can no longer be used for new agreements.`,
      onDelete: async () => {
        await archiveTemplate({ id: template.id });
        toast.success("Template archived");
      },
    });
  }

  return (
    <li className="flex items-center justify-between px-4 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-semibold text-gray-900">
            {template.name}
          </p>
          <Badge color="zinc" className="py-0">
            Rev {template.revision}
          </Badge>
          {template.is_default ? (
            <Badge color="blue" className="py-0">
              Default
            </Badge>
          ) : null}
        </div>
        <p className="mt-0.5 text-xs text-gray-400">
          Updated {template.updated_at ? formatDate(template.updated_at) : "--"}
          {template.createdBy ? ` · created by ${template.createdBy.name}` : ""}
        </p>
      </div>
      <div className="flex shrink-0 gap-1">
        <Button plain onClick={() => openTemplateDialog({ template })}>
          <PencilIcon className="size-4" />
          Edit
        </Button>
        <Button plain onClick={handleDownload} isLoading={isGeneratingPdf}>
          <ArrowDownTrayIcon className="size-4" />
          Download
        </Button>
        <Button plain onClick={handleArchive}>
          <ArchiveBoxIcon className="size-4" />
          Archive
        </Button>
      </div>
    </li>
  );
}
